import { useAtomValue, useSetAtom } from "jotai/index";
import { useCallback, useEffect, useRef, useState } from "react";
import { canv } from "@/canv/global";
import { headerActiveButtonAtom, timerAtom } from "@/interface/AppStore";
import { TIMER_CONFIG } from "@/interface/HeaderBar/RightBar/Timer/constants";
import type { UITimerStatus } from "@/interface/HeaderBar/RightBar/Timer/types";
import {
    calcRemainingSeconds,
    clamp,
    isTimerFinished,
} from "@/interface/HeaderBar/RightBar/Timer/utils";
import { timerController } from "@/timerController";
import { getDateNow } from "@/timerController/utils";

const getAudioUrl = () => `${canv.host}/api/v2/static/media/EndTime2.mp3`;

export const useTimer = () => {
    const timerState = useAtomValue(timerAtom);
    const setActiveHeaderButton = useSetAtom(headerActiveButtonAtom);
    const [currentTime, setCurrentTime] = useState<number>(TIMER_CONFIG.DEFAULT_SECONDS);
    const [status, setStatus] = useState<UITimerStatus>("stop");

    const timerRef = useRef<NodeJS.Timeout | null>(null);
    const startTimeRef = useRef<number>(0);
    const endTimeRef = useRef<number>(0);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const min = String(Math.floor(currentTime / 60));
    const sec = String(currentTime % 60);

    const openCloseTimerWindow = useCallback(
        (name: "timer" | null = null) => {
            setActiveHeaderButton(name);
        },
        [setActiveHeaderButton],
    );

    useEffect(() => {
        audioRef.current = new Audio(getAudioUrl());

        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    const stopTicker = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);
    const startTicker = useCallback(() => {
        stopTicker();

        const tick = () => {
            const remaining = Math.max(
                0,
                Math.round((endTimeRef.current - Date.parse(getDateNow())) / 1000),
            );
            setCurrentTime(remaining);

            if (remaining <= 0) {
                stopTicker();
                timerController.reset();
                audioRef.current?.play();
                setCurrentTime(TIMER_CONFIG.DEFAULT_SECONDS);
                return;
            }
            timerRef.current = setTimeout(tick, TIMER_CONFIG.TICK_INTERVAL);
        };

        timerRef.current = setTimeout(tick, TIMER_CONFIG.TICK_INTERVAL);
    }, [stopTicker]);

    const stop = () => timerController.reset();
    const runPause = () => {
        if (!timerController.timer) {
            timerController.createWithStart(currentTime);
            return;
        }
        if (status === "pause") return timerController.resume();
        if (status === "run") return timerController.pause();
        if (status === "stop") return timerController.restart(currentTime);
    };
    const plusMin = () => {
        setCurrentTime((t) => t + 60);
        if (status !== "stop") {
            timerController.updateDuration(60);
        }
    };

    const handleBlur = () => {
        const minValue = parseInt(min || "0", 10);
        const secValue = parseInt(sec || "0", 10);
        const total = minValue * 60 + secValue;
        setCurrentTime(total);
        canv.appInterface.inputFocused = 0;
    };
    const handleFocus = () => {
        canv.appInterface.inputFocused = 1;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, type: "min" | "sec") => {
        const value = clamp(
            Number(e.target.value.replace(/\D/g, "").slice(0, 2) || 0),
            type === "sec" ? TIMER_CONFIG.MAX_SECONDS : TIMER_CONFIG.MAX_MINUTES,
        );

        setCurrentTime((t) =>
            type === "min" ? value * 60 + (t % 60) : Math.floor(t / 60) * 60 + value,
        );
    };

    const setDefaultParams = useCallback(() => {
        setStatus("stop");
        setCurrentTime(TIMER_CONFIG.DEFAULT_SECONDS);
    }, []);

    useEffect(() => {
        stopTicker();
        if (!timerController.timer) {
            setDefaultParams();
            return;
        }

        if (timerState.startedAt !== null && timerState.pausedAt === null) {
            const remaining = calcRemainingSeconds(timerState.estimatedStopTime);

            if (remaining <= 0) {
                timerController.reset();
                return;
            }

            setCurrentTime(remaining);
            setStatus("run");
            openCloseTimerWindow("timer");

            startTimeRef.current = Date.parse(getDateNow());
            endTimeRef.current = startTimeRef.current + remaining * 1000;

            startTicker();
        } else if (timerState.startedAt === null || timerState.duration === 0) {
            if (timerState.estimatedStopTime && isTimerFinished(timerState.estimatedStopTime)) {
                setStatus("stop");
                audioRef.current?.play();
                timerController.reset();
                return;
            }
            setDefaultParams();
        } else if (timerState.pausedAt !== null) {
            setStatus("pause");
            setCurrentTime(timerState.remainingAfterPause || TIMER_CONFIG.DEFAULT_SECONDS);
            openCloseTimerWindow("timer");
        }
    }, [timerState, setDefaultParams, startTicker, stopTicker, openCloseTimerWindow]);

    const isReadOnlyInput = status !== "stop";

    return {
        status,
        min,
        sec,
        runPause,
        plusMin,
        openCloseTimerWindow,
        stop,
        handleInputChange,
        handleBlur,
        handleFocus,
        isReadOnlyInput,
    };
};
